import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { X, Save, Plus, Trash2, Loader2 } from "lucide-react";
import type { RateLimitEntry } from "./RateLimits";

type RateLimitsByType = {
  [type: string]: {
    [interval: number]: number;
  };
};

type Props = {
  queryLimits: RateLimitEntry[];
  onSave: (limits: RateLimitEntry[]) => Promise<void>;
  onCancel: () => void;
};

const groupLimitsByType = (limits: RateLimitEntry[]): RateLimitsByType => {
  const grouped: RateLimitsByType = {};

  for (const limit of limits) {
    if (!grouped[limit.type]) {
      grouped[limit.type] = {};
    }
    grouped[limit.type]![limit.interval] = limit.limit;
  }

  return grouped;
};

export function RateLimitEditor({ queryLimits, onSave, onCancel }: Props) {
  const [isSaving, setIsSaving] = useState(false);
  const [editedLimits, setEditedLimits] = useState<RateLimitsByType>(groupLimitsByType(queryLimits));
  const [newRateLimitType, setNewRateLimitType] = useState("");
  const [newDurationSeconds, setNewDurationSeconds] = useState("");
  const [newLimit, setNewLimit] = useState("0");

  const intervals = Array.from(
    new Set(
      Object.values(editedLimits).flatMap(typeIntervals =>
        Object.keys(typeIntervals).map(k => parseInt(k, 10))
      )
    )
  ).sort((a, b) => a - b);

  const types = Object.keys(editedLimits).sort();

  const handleLimitChange = (type: string, interval: number, value: string) => {
    if (value === "") return;
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;

    setEditedLimits(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [interval]: numValue,
      },
    }));
  };

  const clearLimit = (type: string, interval: number) => {
    setEditedLimits(prev => {
      const updated = { ...prev };
      if (updated[type]) {
        const typeLimits = { ...updated[type] };
        delete typeLimits[interval];
        updated[type] = typeLimits;
      }
      return updated;
    });
  };

  const addNewDuration = () => {
    const seconds = parseInt(newDurationSeconds, 10);
    if (isNaN(seconds) || seconds < 1) return;

    const currentIntervals = Array.from(
      new Set(
        Object.values(editedLimits).flatMap(typeIntervals =>
          Object.keys(typeIntervals).map(k => parseInt(k, 10))
        )
      )
    );
    if (currentIntervals.includes(seconds)) return;

    setEditedLimits(prev => {
      const updated = { ...prev };
      for (const type of Object.keys(updated)) {
        updated[type] = {
          ...updated[type],
          [seconds]: 0,
        };
      }
      return updated;
    });

    setNewDurationSeconds("");
  };

  const addNewRateLimitType = () => {
    if (!newRateLimitType.trim()) return;

    const type = newRateLimitType.trim().toLowerCase();
    if (editedLimits[type]) return;

    const newLimits: { [interval: number]: number } = {};
    for (const interval of intervals) {
      newLimits[interval] = 0;
    }

    setEditedLimits(prev => ({
      ...prev,
      [type]: newLimits,
    }));

    setNewRateLimitType("");
  };

  const removeRateLimitType = (type: string) => {
    setEditedLimits(prev => {
      const updated = { ...prev };
      delete updated[type];
      return updated;
    });
  };

  const removeDuration = (interval: number) => {
    setEditedLimits(prev => {
      const updated = { ...prev };
      for (const type of Object.keys(updated)) {
        const typeLimits = { ...updated[type] };
        delete typeLimits[interval];
        updated[type] = typeLimits;
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);

    const limits: RateLimitEntry[] = [];
    for (const [type, typeIntervals] of Object.entries(editedLimits)) {
      for (const [interval, limit] of Object.entries(typeIntervals)) {
        limits.push({
          type,
          limit,
          interval: parseInt(interval, 10),
        });
      }
    }

    try {
      await onSave(limits);
    } catch (error) {
      console.error("Failed to save rate limits:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-core/50">
            <TableHead className="font-bold text-silica">Type</TableHead>
            <TableHead className="font-bold text-silica">Duration (seconds)</TableHead>
            <TableHead className="font-bold text-silica">Limit</TableHead>
            <TableHead className="w-20 font-bold text-silica">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(editedLimits).flatMap(([type, typeIntervals]) =>
            Object.entries(typeIntervals).map(([intervalStr, limit]) => {
              const interval = parseInt(intervalStr, 10);
              return (
                <TableRow key={`${type}-${interval}`}>
                  <TableCell>
                    <Input
                      value={type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        if (!newType) return;
                        setEditedLimits(prev => {
                          const updated = { ...prev };
                          const intervals = { ...updated[type] };
                          delete updated[type];
                          updated[newType] = intervals;
                          return updated;
                        });
                      }}
                      className="bg-obsidian border-slate-core"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="1"
                      value={interval}
                      onChange={(e) => {
                        const newInterval = parseInt(e.target.value, 10);
                        if (isNaN(newInterval) || newInterval < 1) return;
                        setEditedLimits(prev => {
                          const updated = { ...prev };
                          const typeIntervals = { ...updated[type] };
                          delete typeIntervals[interval];
                          typeIntervals[newInterval] = limit;
                          updated[type] = typeIntervals;
                          return updated;
                        });
                      }}
                      className="bg-obsidian border-slate-core"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      value={limit}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value, 10);
                        if (isNaN(newLimit) || newLimit < 0) return;
                        setEditedLimits(prev => ({
                          ...prev,
                          [type]: {
                            ...prev[type],
                            [interval]: newLimit,
                          },
                        }));
                      }}
                      className="bg-obsidian border-slate-core"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => clearLimit(type, interval)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
          <TableRow>
            <TableCell>
              <Input
                placeholder="type"
                value={newRateLimitType}
                onChange={(e) => setNewRateLimitType(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
            </TableCell>
            <TableCell>
              <Input
                type="number"
                min="1"
                placeholder="seconds"
                value={newDurationSeconds}
                onChange={(e) => setNewDurationSeconds(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
            </TableCell>
            <TableCell>
              <Input
                type="number"
                min="0"
                placeholder="limit"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
            </TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (newRateLimitType.trim() && newDurationSeconds && !isNaN(parseInt(newDurationSeconds, 10))) {
                    const type = newRateLimitType.trim();
                    const seconds = parseInt(newDurationSeconds, 10);
                    const limitValue = parseInt(newLimit, 10);
                    setEditedLimits(prev => ({
                      ...prev,
                      [type]: {
                        ...prev[type],
                        [seconds]: isNaN(limitValue) ? 0 : limitValue,
                      },
                    }));
                    setNewRateLimitType("");
                    setNewDurationSeconds("");
                    setNewLimit("0");
                  }
                }}
                disabled={!newRateLimitType.trim() || !newDurationSeconds || isNaN(parseInt(newDurationSeconds, 10)) || parseInt(newDurationSeconds, 10) < 1}
                className="text-neon-green hover:text-neon-green/80"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div className="mt-4 flex justify-end gap-2 pt-4 border-t border-slate-core">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save Changes
        </Button>
      </div>
    </>
  );
}
